import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

const SESSION_KEY = "exora_chat_session";

function getSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = "sess_" + Math.random().toString(36).slice(2); localStorage.setItem(SESSION_KEY, s); }
  return s;
}

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Exora 🌿 your pest control assistant. Ask me about a pest problem or how to book a free inspection." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSession(), message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: acc }; return c; });
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Sorry, I'm offline right now. Please call +1 (672) 377-5455." }; return c; });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-brand-green text-white shadow-xl flex items-center justify-center hover:bg-brand-greenHover hover:scale-105 transition-transform"
        aria-label="Open chat assistant"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div data-testid="chat-window" className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-sm h-[520px] bg-white rounded-3xl shadow-2xl border border-black/5 flex flex-col overflow-hidden animate-fade-up">
          <div className="bg-brand-green px-5 py-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-brand-gold flex items-center justify-center"><Sparkles className="h-5 w-5 text-brand-green" /></div>
            <div><p className="text-white font-heading font-bold leading-tight">Exora Assistant</p><p className="text-white/60 text-xs">Typically replies instantly</p></div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-brand-light">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.role === "user" ? "bg-brand-green text-white rounded-br-sm" : "bg-white border border-black/5 text-brand-charcoal rounded-bl-sm"
                }`}>
                  {m.content || <span className="inline-flex gap-1"><span className="animate-pulse">●</span></span>}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-black/5 flex items-center gap-2 bg-white">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about a pest problem..."
              className="flex-1 px-4 py-2.5 rounded-full bg-brand-light text-sm outline-none focus:ring-2 focus:ring-brand-green/30"
            />
            <button data-testid="chat-send-btn" onClick={send} disabled={busy}
              className="h-10 w-10 rounded-full bg-brand-gold text-brand-green flex items-center justify-center hover:bg-brand-goldHover disabled:opacity-50 transition-colors">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
